import React from 'react';
import { motion } from 'framer-motion';
import { FULL, H, HMI_STATE, W } from './screenData';

// Colours of the mock panel (it is always dark, like most industrial HMIs).
const C = {
    bg: '#0E1726',
    bar: '#0A1120',
    panel: '#16213A',
    edge: '#26345A',
    row: '#1B2742',
    text: '#E6ECF5',
    sub: '#8A9BB8',
    accent: '#38BDF8',
    green: '#22C55E',
    red: '#EF4444',
    amber: '#F59E0B',
    off: '#475569',
};
export const FRAME_BG = '#0B1220';

const SANS = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Inter, sans-serif';
const MONO = '"SF Mono", "JetBrains Mono", Menlo, Consolas, monospace';
const EASE = [0.22, 1, 0.36, 1];
const TABS = ['Overview', 'Compressor', 'Cooling', 'Trends', 'Parameters'];

function Card({ x, y, w, h, title }) {
    return (
        <g>
            <rect x={x} y={y} width={w} height={h} rx={12} fill={C.panel} stroke={C.edge} />
            <text x={x + 16} y={y + 30} fill={C.text} fontSize={16} fontWeight={600}>{title}</text>
        </g>
    );
}

function Toggle({ x, y, on }) {
    return (
        <g>
            <rect x={x} y={y} width={44} height={24} rx={12} fill={on ? C.green : C.off} />
            <circle cx={on ? x + 32 : x + 12} cy={y + 12} r={9} fill="#fff" />
        </g>
    );
}

function DeviceCard({ x, title, on, auto, rpm }) {
    const y = 344;
    return (
        <g>
            <Card x={x} y={y} w={370} h={200} title={title} />
            <rect x={x + 290} y={y + 12} width={64} height={26} rx={13} fill={on ? C.green : C.off} />
            <text x={x + 322} y={y + 30} textAnchor="middle" fill="#fff" fontSize={13} fontWeight={700}>{on ? 'On' : 'Off'}</text>
            <line x1={x + 16} x2={x + 354} y1={y + 100} y2={y + 100} stroke={C.edge} />
            <line x1={x + 16} x2={x + 354} y1={y + 144} y2={y + 144} stroke={C.edge} />
            <text x={x + 16} y={y + 83} fill={C.sub} fontSize={15}>Auto Mode</text>
            <Toggle x={x + 306} y={y + 66} on={auto} />
            <text x={x + 16} y={y + 127} fill={C.sub} fontSize={15}>Manual Output</text>
            <Toggle x={x + 306} y={y + 110} on={false} />
            <text x={x + 16} y={y + 171} fill={C.sub} fontSize={15}>Speed</text>
            <text x={x + 354} y={y + 171} textAnchor="end" fill={C.text} fontSize={15} fontFamily={MONO}>{rpm} rpm</text>
        </g>
    );
}

function ValueRow({ x, y, label, value, color = C.text }) {
    return (
        <g>
            <text x={x + 16} y={y} fill={C.sub} fontSize={15}>{label}</text>
            <text x={x + 380} y={y} textAnchor="end" fill={color} fontSize={16} fontFamily={MONO}>{value}</text>
        </g>
    );
}

export function HmiScreen({ s }) {
    const p = Number(s.pressure);
    const unitX = 40 + s.pressure.length * 36 + 8;
    return (
        <g fontFamily={SANS}>
            <rect x={-2000} y={-2000} width={W + 4000} height={H + 4000} fill={C.bg} />

            {/* header */}
            <rect width={W} height={64} fill={C.bar} />
            <text x={22} y={37} fill={C.sub} fontSize={12} fontWeight={700} letterSpacing={1.5}>STATUS</text>
            <rect x={86} y={18} width={100} height={28} rx={14} fill={s.statusColor} />
            <text x={136} y={37} textAnchor="middle" fill="#fff" fontSize={13} fontWeight={700} letterSpacing={1}>{s.status}</text>
            <text x={640} y={40} textAnchor="middle" fill={C.text} fontSize={20} fontWeight={600}>Overview</text>
            <text x={1110} y={38} textAnchor="end" fill={C.sub} fontSize={13}>operator</text>
            <text x={1200} y={38} textAnchor="end" fill={C.text} fontSize={14} fontFamily={MONO}>14:32:08</text>

            {/* right sidebar */}
            <rect x={1216} y={64} width={64} height={672} fill={C.bar} />
            <path d="M1248 100 L1268 130 L1228 130 Z" fill="none" stroke={C.amber} strokeWidth={2.5} strokeLinejoin="round" />
            <line x1={1248} y1={110} x2={1248} y2={120} stroke={C.amber} strokeWidth={2.5} strokeLinecap="round" />
            <circle cx={1248} cy={125} r={1.6} fill={C.amber} />
            <circle cx={1262} cy={90} r={11} fill={C.red} />
            <text x={1262} y={94.5} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={700}>{s.alarms}</text>
            <path d="M1234 186 L1248 172 L1262 186 V200 H1234 Z" fill="none" stroke={C.sub} strokeWidth={2} strokeLinejoin="round" />
            <rect x={1236} y={226} width={24} height={24} rx={4} fill="none" stroke={C.sub} strokeWidth={2} />
            <path d="M1242 238 h12 M1248 232 v12" stroke={C.sub} strokeWidth={2} />
            <circle cx={1248} cy={296} r={10} fill="none" stroke={C.sub} strokeWidth={2} />
            <circle cx={1248} cy={296} r={3.5} fill={C.sub} />
            <circle cx={1248} cy={348} r={6} fill="none" stroke={C.sub} strokeWidth={2} />
            <path d="M1236 368 Q1248 352 1260 368" fill="none" stroke={C.sub} strokeWidth={2} />

            {/* bottom navigation */}
            <rect x={0} y={736} width={1216} height={64} fill={C.bar} />
            {TABS.map((tab, i) => {
                const x = i * (1216 / 5);
                const active = i === 0;
                return (
                    <g key={tab}>
                        {active && <rect x={x} y={736} width={1216 / 5} height={3} fill={C.accent} />}
                        <text x={x + 1216 / 10} y={774} textAnchor="middle" fill={active ? C.accent : C.sub}
                              fontSize={15} fontWeight={active ? 700 : 500}>{tab}</text>
                    </g>
                );
            })}

            {/* discharge pressure */}
            <Card x={24} y={88} w={370} h={240} title="Discharge Pressure" />
            <text x={40} y={200} fill={C.text} fontSize={60} fontWeight={600} fontFamily={MONO}>{s.pressure}</text>
            <text x={unitX} y={200} fill={C.sub} fontSize={20}>bar</text>
            <rect x={40} y={232} width={338} height={8} rx={4} fill={C.off} />
            <rect x={40} y={232} width={Math.max(4, Math.min(1, p / 45) * 338)} height={8} rx={4} fill={C.accent} />
            <text x={40} y={282} fill={C.sub} fontSize={14}>SP</text>
            <text x={70} y={282} fill={C.text} fontSize={15} fontFamily={MONO}>40.0 bar</text>
            <text x={210} y={282} fill={C.sub} fontSize={14}>Hi</text>
            <text x={236} y={282} fill={C.text} fontSize={15} fontFamily={MONO}>42.0 bar</text>

            {/* loading */}
            <Card x={410} y={88} w={370} h={240} title="Loading" />
            <text x={426} y={172} fill={s.mode === 'LOAD MODE' ? C.accent : C.amber} fontSize={24} fontWeight={700}
                  letterSpacing={1.5}>{s.mode}</text>
            {[['full', 'Full Load'], ['half', 'Half Load'], ['unload', 'Unload']].map(([id, label], i) => {
                const x = 426 + 116 * i;
                const on = s.load === id;
                return (
                    <g key={id}>
                        <rect x={x} y={236} width={106} height={44} rx={8} fill={on ? C.accent : 'none'} stroke={on ? C.accent : C.edge} strokeWidth={1.5} />
                        <text x={x + 53} y={263} textAnchor="middle" fill={on ? FRAME_BG : C.sub} fontSize={14} fontWeight={on ? 700 : 500}>{label}</text>
                    </g>
                );
            })}

            {/* latest alarms */}
            <Card x={796} y={88} w={396} h={240} title="Latest Alarms" />
            {s.alarm ? (
                <g>
                    <rect x={812} y={140} width={364} height={40} rx={6} fill="#7F1D1D" />
                    <text x={826} y={166} fill="#FEE2E2" fontSize={15} fontWeight={600}>{s.alarm}</text>
                    <text x={1166} y={166} textAnchor="end" fill="#FCA5A5" fontSize={12} fontFamily={MONO}>14:31:52</text>
                </g>
            ) : (
                <g>
                    <circle cx={828} cy={160} r={9} fill="rgba(34,197,94,0.18)" stroke={C.green} strokeWidth={1.5} />
                    <path d="M823.5 160.5 l3 3 l6 -6.5" fill="none" stroke={C.green} strokeWidth={2} strokeLinecap="round" />
                    <text x={846} y={166} fill={C.sub} fontSize={15}>No active alarms</text>
                </g>
            )}
            {['Low oil level', 'Filter ΔP high', 'Door open'].map((label, i) => (
                <g key={label}>
                    <rect x={812} y={196 + i * 40} width={364} height={32} rx={6} fill={C.row} />
                    <text x={826} y={217 + i * 40} fill={C.sub} fontSize={13}>{label} · cleared</text>
                    <text x={1166} y={217 + i * 40} textAnchor="end" fill={C.sub} fontSize={12} fontFamily={MONO}>{`1${3 - i}:0${i + 2}:4${i}`}</text>
                </g>
            ))}

            {/* cooling devices */}
            <DeviceCard x={24} title="Circulation Pump" on={s.pump === 'On'} auto={s.auto} rpm={s.pumpRpm} />
            <DeviceCard x={410} title="Tower Fan" on={s.fan === 'On'} auto={s.auto} rpm={s.fanRpm} />

            {/* motor winding */}
            <Card x={796} y={344} w={396} h={200} title="Motor Winding Temp" />
            <ValueRow x={796} y={427} label="Phase A" value={`${s.windingA} °C`} color={Number(s.windingA) > 70 ? C.amber : C.text} />
            <ValueRow x={796} y={471} label="Warn" value="170 °C" />
            <ValueRow x={796} y={515} label="Trip" value="180 °C" />
            <line x1={812} x2={1176} y1={444} y2={444} stroke={C.edge} />
            <line x1={812} x2={1176} y1={488} y2={488} stroke={C.edge} />

            {/* compressor motor */}
            <Card x={24} y={560} w={756} h={152} title="Compressor Motor" />
            <circle cx={52} cy={628} r={8} fill={s.motor === 'Running' ? C.green : C.off} />
            <text x={70} y={638} fill={s.motor === 'Running' ? C.text : C.sub} fontSize={28} fontWeight={700}>{s.motor}</text>
            <text x={420} y={616} fill={C.sub} fontSize={13}>Run hours</text>
            <text x={420} y={650} fill={C.text} fontSize={24} fontFamily={MONO}>{s.hours} h</text>
            <text x={600} y={616} fill={C.sub} fontSize={13}>Starts today</text>
            <text x={600} y={650} fill={C.text} fontSize={24} fontFamily={MONO}>3</text>

            {/* cooling water */}
            <Card x={796} y={560} w={396} h={152} title="Cooling Water" />
            <ValueRow x={796} y={629} label="Inlet" value={`${s.inlet} °C`} />
            <ValueRow x={796} y={669} label="Outlet" value={`${s.outlet} °C`} />
            <line x1={812} x2={1176} y1={644} y2={644} stroke={C.edge} />
        </g>
    );
}

function holePath([x, y, w, h]) {
    return `M-4000 -4000 H${W + 4000} V${H + 4000} H-4000 Z M${x} ${y} h${w} v${h} h${-w} Z`;
}

/**
 * The mock screen inside a fixed 16:10 frame. `view` (x, y, w, h) zooms with an
 * animated viewBox; `keep` darkens everything outside one box (opacity 1 = a crop).
 */
export default function HmiView({
    state = 'ready', view = FULL, keep = null, keepOpacity = 0, children, label, duration = 0.8,
}) {
    const vb = view.join(' ');
    return (
        <motion.svg
            viewBox={vb}
            initial={false}
            animate={{ viewBox: vb }}
            transition={{ duration, ease: EASE }}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={label}
            style={{ display: 'block', width: '100%', height: '100%' }}
        >
            <HmiScreen s={HMI_STATE[state]} />
            {keep && (
                <motion.path
                    d={holePath(keep)}
                    fill={FRAME_BG}
                    fillRule="evenodd"
                    initial={false}
                    animate={{ opacity: keepOpacity }}
                    transition={{ duration: 0.5 }}
                />
            )}
            {children}
        </motion.svg>
    );
}

/** A static cut-out of the mock screen at its own aspect (sample images, crops). */
export function HmiCrop({ state = 'ready', box, children, label }) {
    const [x, y, w, h] = box;
    return (
        <svg viewBox={`${x} ${y} ${w} ${h}`} role="img" aria-label={label}
             style={{ display: 'block', width: '100%', height: 'auto' }}>
            <HmiScreen s={HMI_STATE[state]} />
            {children}
        </svg>
    );
}

/** Outline drawn on top of the screen, in screen pixels. */
export function Outline({ box, color, dashed = false, width = 3, fill = 'none', opacity = 1, animateBox = true }) {
    const [x, y, w, h] = box;
    return (
        <motion.rect
            initial={false}
            animate={animateBox ? { attrX: x, attrY: y, width: w, height: h, opacity } : { opacity }}
            x={x} y={y} width={w} height={h}
            transition={{ duration: 0.45, ease: EASE }}
            rx={6}
            fill={fill}
            stroke={color}
            strokeWidth={width}
            strokeDasharray={dashed ? '8 6' : undefined}
            vectorEffect="non-scaling-stroke"
        />
    );
}

/** A small label chip above a box, in screen pixels. */
export function BoxLabel({ box, text, color, size = 15 }) {
    const [x, y] = box;
    const w = text.length * size * 0.62 + 16;
    const ty = y > 30 ? y - 28 : y + 4;
    return (
        <g>
            <rect x={x} y={ty} width={w} height={size + 10} rx={5} fill={color} />
            <text x={x + 8} y={ty + size + 2} fill={FRAME_BG} fontSize={size} fontWeight={700} fontFamily={MONO}>{text}</text>
        </g>
    );
}
